/**
 * Whether a plant belongs on this site — and if not, why not.
 *
 * ## Classify, never block
 *
 * Everything here produces a *label and a reason*. Nothing produces a refusal. A designer who wants a
 * fig in Vermont has a reason, is not confused about the winter, and is better served by a badge
 * saying "winter hardiness concern" than by a catalog that pretends the fig does not exist. Zone maps
 * are averages, gardens have walls and south-facing brick, and the person holding the drawing knows
 * things the profile does not.
 *
 * So the output is four ratings, ordered by how much they should make you look twice —
 * `ideal`, `suitable`, `marginal`, `outside-range` — plus warnings carrying a code, a severity and a
 * sentence. The UI shows the badge; the badge never disables the tile.
 *
 * ## Where the ratings come from
 *
 * Each check produces a concern at one of three severities, and the rating is the worst of them. That
 * is deliberately simple and deliberately explainable: a plant is marginal *because* of a named
 * thing, and the sentence saying which is the actual product. A weighted score summing hardiness and
 * heat into one number would rank plants beautifully and tell nobody anything.
 *
 * Checks that lack data do not fire. A record with no `climate.maximumHeatZone` raises no heat
 * warning, a site with no `heatZone` raises none either, and neither counts against the rating —
 * missing data must never masquerade as a passed test or as a failed one.
 */

import type { FloraDefinition } from '@/flora/FloraDefinition';
import { seasonFitsPlant } from './Phenology';
import {
  effectiveZoneValue,
  isLocalized,
  nativeRegionLabel,
  zoneFromValue,
  zoneLabel,
  type NativeRegionCode,
  type SiteLocalization,
} from './SiteLocalization';

export type SuitabilityRating = 'ideal' | 'suitable' | 'marginal' | 'outside-range';

export const SUITABILITY_RATINGS: readonly SuitabilityRating[] = [
  'ideal',
  'suitable',
  'marginal',
  'outside-range',
];

const RATING_LABELS: Record<SuitabilityRating, string> = {
  ideal: 'Ideal',
  suitable: 'Suitable',
  marginal: 'Marginal',
  'outside-range': 'Outside recommended range',
};

export function suitabilityLabel(rating: SuitabilityRating): string {
  return RATING_LABELS[rating];
}

/** Short form for a badge on a palette tile, where there is room for one word. */
export function suitabilityBadge(rating: SuitabilityRating): string {
  return rating === 'outside-range' ? 'Out of range' : RATING_LABELS[rating];
}

export type SuitabilityWarningCode =
  | 'winter-hardiness'
  | 'mild-winter'
  | 'summer-heat'
  | 'growing-season'
  | 'moisture'
  | 'regional-concern';

/** `note` is worth knowing, `caution` makes a plant marginal, `serious` puts it outside the range. */
export type WarningSeverity = 'note' | 'caution' | 'serious';

export interface SuitabilityWarning {
  code: SuitabilityWarningCode;
  severity: WarningSeverity;
  /** Four or five words for a badge tooltip — `Winter hardiness concern`. */
  headline: string;
  /** One sentence saying what will actually happen. */
  message: string;
}

/** Whether the plant is from here, from elsewhere, or a documented problem here. */
export type NativeStatus = 'native' | 'introduced' | 'invasive' | 'unknown';

const NATIVE_STATUS_LABELS: Record<NativeStatus, string> = {
  native: 'Native here',
  introduced: 'Introduced',
  invasive: 'Invasive here',
  unknown: 'Range not recorded',
};

export function nativeStatusLabel(status: NativeStatus): string {
  return NATIVE_STATUS_LABELS[status];
}

export interface SuitabilityAssessment {
  rating: SuitabilityRating;
  warnings: SuitabilityWarning[];
  /**
   * Zone values of slack above the plant's cold limit. 2 means the site is two full zones warmer
   * than the coldest winter the plant survives; negative means the winter is colder than it takes.
   */
  coldHeadroom: number;
  /** The same at the warm end, or `null` when the record states no upper limit. */
  heatHeadroom: number | null;
  nativeStatus: NativeStatus;
  /** True when the assessment ran against a real profile rather than the unlocalized default. */
  localized: boolean;
}

/** Warnings only ever move the rating one way, and this is the order. */
const RATING_BY_SEVERITY: Record<WarningSeverity, SuitabilityRating> = {
  note: 'suitable',
  caution: 'marginal',
  serious: 'outside-range',
};

const RATING_ORDER: Record<SuitabilityRating, number> = {
  ideal: 0,
  suitable: 1,
  marginal: 2,
  'outside-range': 3,
};

/**
 * The plant's cold limit as a zone value.
 *
 * `climate.minimumZone` is the precise statement; `hardinessZones.minimum` is the whole-zone figure
 * the catalog has always carried, and reading `4` as `4a` is the conservative interpretation — a
 * record that says "zone 4" is claiming the whole of zone 4, cold half included.
 */
export function minimumZoneOf(definition: FloraDefinition): number | undefined {
  return definition.climate?.minimumZone ?? definition.hardinessZones?.minimum;
}

/**
 * The warm limit, read the other way: `9` means "through the whole of zone 9", so 9.5.
 *
 * The asymmetry is the point. Both readings widen the plant's stated range rather than narrowing it,
 * which is what keeps a warning from firing on a plant whose record was simply written in whole
 * zones.
 */
export function maximumZoneOf(definition: FloraDefinition): number | undefined {
  const explicit = definition.climate?.maximumZone;
  if (explicit !== undefined) return explicit;
  const coarse = definition.hardinessZones?.maximum;
  return coarse === undefined ? undefined : coarse + 0.5;
}

/** Regions dry enough that a moisture-sensitive plant needs irrigation to be honest about. */
const ARID_REGIONS: readonly NativeRegionCode[] = ['southwest', 'great-plains', 'california'];

/**
 * Assess one plant against one site.
 *
 * Pure. Called through `FloraResolver`, which caches it — never call it in a render loop directly.
 */
export function assessSuitability(
  definition: FloraDefinition,
  localization: SiteLocalization,
  options: { zoneAdjustment?: number; irrigated?: boolean } = {},
): SuitabilityAssessment {
  const warnings: SuitabilityWarning[] = [];
  const siteZone = effectiveZoneValue(localization) + (options.zoneAdjustment ?? 0);
  const slack = definition.climate?.zoneToleranceSlack ?? 0.5;

  const minimumZone = minimumZoneOf(definition);
  const maximumZone = maximumZoneOf(definition);
  const coldHeadroom = minimumZone === undefined ? Number.POSITIVE_INFINITY : siteZone - minimumZone;
  const heatHeadroom = maximumZone === undefined ? null : maximumZone - siteZone;

  /* ------------------------------- Winter cold ------------------------------- */
  if (minimumZone !== undefined && coldHeadroom < 0.5) {
    const deficit = -coldHeadroom;
    const limit = zoneLabel(zoneFromValue(minimumZone));
    if (deficit > slack) {
      warnings.push({
        code: 'winter-hardiness',
        severity: 'serious',
        headline: 'Winter hardiness concern',
        message:
          `Hardy to zone ${limit}; this site is zone ${zoneLabel(localization.hardinessZone)}. ` +
          'Expect winter kill in an average year without protection.',
      });
    } else if (deficit > 0) {
      warnings.push({
        code: 'winter-hardiness',
        severity: 'caution',
        headline: 'Winter hardiness concern',
        message:
          `Rated to zone ${limit}, just below this site's winter. It will come through mild ` +
          'winters and may be cut back or lost in a hard one.',
      });
    } else {
      warnings.push({
        code: 'winter-hardiness',
        severity: 'note',
        headline: 'At its cold limit',
        message: `Zone ${limit} is the coldest this plant takes, and this site is at that edge.`,
      });
    }
  }

  /* ------------------------------- Mild winter ------------------------------- */
  // The other end of the same axis, and a genuinely different failure: a lilac in Florida does not
  // freeze, it simply never gets the chill it needs and stops flowering.
  if (maximumZone !== undefined && heatHeadroom !== null && heatHeadroom < 0.5) {
    const excess = -heatHeadroom;
    const limit = zoneLabel(zoneFromValue(maximumZone));
    if (excess > slack) {
      warnings.push({
        code: 'mild-winter',
        severity: 'serious',
        headline: 'Winter too mild',
        message:
          `Grown through zone ${limit}; this site is warmer. Without enough winter chill it is ` +
          'likely to flower poorly and decline.',
      });
    } else if (excess > 0) {
      warnings.push({
        code: 'mild-winter',
        severity: 'caution',
        headline: 'Winter too mild',
        message: `Rated through zone ${limit}. Flowering may be thin in a warm winter.`,
      });
    } else {
      warnings.push({
        code: 'mild-winter',
        severity: 'note',
        headline: 'At its warm limit',
        message: `Zone ${limit} is the warmest this plant is grown in, and this site is at that edge.`,
      });
    }
  }

  /* -------------------------------- Summer heat ------------------------------- */
  const siteHeat = localization.heatZone;
  const plantHeat = definition.climate?.maximumHeatZone;
  if (siteHeat !== undefined && plantHeat !== undefined && siteHeat > plantHeat - 1) {
    const over = siteHeat - plantHeat;
    if (over >= 2) {
      warnings.push({
        code: 'summer-heat',
        severity: 'serious',
        headline: 'Excessive summer heat',
        message:
          `Tolerates heat zone ${plantHeat}; this site is heat zone ${siteHeat}. Expect scorch, ` +
          'early dormancy, and a short life.',
      });
    } else if (over >= 1) {
      warnings.push({
        code: 'summer-heat',
        severity: 'caution',
        headline: 'Excessive summer heat',
        message:
          `Rated to heat zone ${plantHeat}. This site runs hotter — give it afternoon shade and ` +
          'reliable water.',
      });
    } else {
      warnings.push({
        code: 'summer-heat',
        severity: 'note',
        headline: 'At its heat limit',
        message: `Heat zone ${plantHeat} is this plant's ceiling, and this site sits at it.`,
      });
    }
  }

  /* ------------------------------ Growing season ------------------------------ */
  const season = seasonFitsPlant(definition, localization);
  if (season && !season.fits) {
    const shortfall = season.requiredDays - season.availableDays;
    warnings.push({
      code: 'growing-season',
      severity: shortfall > 30 ? 'serious' : 'caution',
      headline: 'Insufficient growing season',
      message:
        `Needs about ${season.requiredDays} frost-free days; this site has ` +
        `${season.availableDays}. Growth will not harden off before the first frost.`,
    });
  }

  /* --------------------------------- Moisture --------------------------------- */
  // A proxy, and labelled as one in the docs: region stands in for rainfall until a profile carries
  // `annualPrecipitationMm`, at which point this check reads that instead and nothing else changes.
  if (
    definition.climate?.moistureSensitive === true &&
    options.irrigated !== true &&
    localization.conditions?.irrigated !== true &&
    ARID_REGIONS.includes(localization.nativeRegion)
  ) {
    warnings.push({
      code: 'moisture',
      severity: 'caution',
      headline: 'Moisture mismatch',
      message:
        'This plant needs consistently moist ground, and this region is dry. Plan on irrigation.',
    });
  }

  /* -------------------------------- Regional ---------------------------------- */
  const nativeStatus = resolveNativeStatus(definition, localization.nativeRegion);
  if (nativeStatus === 'invasive') {
    warnings.push({
      code: 'regional-concern',
      severity: 'caution',
      headline: 'Regional concern',
      message:
        `Documented as invasive in the ${nativeRegionLabel(localization.nativeRegion)}. It will ` +
        'spread beyond where it is planted.',
    });
  }

  /* --------------------------------- Rating ----------------------------------- */
  let rating: SuitabilityRating = 'ideal';
  for (const warning of warnings) {
    const candidate = RATING_BY_SEVERITY[warning.severity];
    if (RATING_ORDER[candidate] > RATING_ORDER[rating]) rating = candidate;
  }
  // `ideal` is a claim, not merely the absence of a complaint: it means the site sits comfortably
  // inside the plant's range rather than just inside it. Without a profile nothing has been checked
  // at all, so an unlocalized site reports every plant as plainly suitable.
  const localized = isLocalized(localization);
  if (rating === 'ideal' && (!localized || coldHeadroom < 1)) rating = 'suitable';

  return { rating, warnings, coldHeadroom, heatHeadroom, nativeStatus, localized };
}

/**
 * Native, introduced, invasive, or not recorded — for *this* region.
 *
 * Checked invasive-first: a plant listed as both native somewhere in a region and invasive in it is
 * being flagged for a reason, and the flag is the answer that changes a decision.
 */
export function resolveNativeStatus(
  definition: FloraDefinition,
  region: NativeRegionCode,
): NativeStatus {
  const range = definition.nativeRange;
  if (!range) return 'unknown';
  if (range.invasive?.includes(region)) return 'invasive';
  if (range.native?.includes(region)) return 'native';
  if (range.introduced?.includes(region)) return 'introduced';
  // A record that lists native regions and does not list this one is making a statement about this
  // one: the plant is not from here. Absent lists say nothing, which is what `unknown` is for.
  if (range.native !== undefined || range.introduced !== undefined) return 'introduced';
  return 'unknown';
}

/** The most severe warning, for a one-line summary. */
export function primaryWarning(
  assessment: SuitabilityAssessment,
): SuitabilityWarning | undefined {
  const order: Record<WarningSeverity, number> = { note: 0, caution: 1, serious: 2 };
  let best: SuitabilityWarning | undefined;
  for (const warning of assessment.warnings) {
    if (!best || order[warning.severity] > order[best.severity]) best = warning;
  }
  return best;
}

/** Counts for the palette's `642 Ideal / Suitable · 74 Marginal · 31 Outside range` summary. */
export interface SuitabilityTally {
  ideal: number;
  suitable: number;
  marginal: number;
  outsideRange: number;
  total: number;
}

export function tallySuitability(ratings: Iterable<SuitabilityRating>): SuitabilityTally {
  const tally: SuitabilityTally = { ideal: 0, suitable: 0, marginal: 0, outsideRange: 0, total: 0 };
  for (const rating of ratings) {
    tally.total += 1;
    if (rating === 'ideal') tally.ideal += 1;
    else if (rating === 'suitable') tally.suitable += 1;
    else if (rating === 'marginal') tally.marginal += 1;
    else tally.outsideRange += 1;
  }
  return tally;
}
